import { Immutable } from "immer";
import React, { useId } from "react";

import type { SharedAccordionBlock as Value } from "@reactivated";

import { RichTextBlock } from "@thelabnyc/thelabui/src/components/RichText";
import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { getGlobalSpacingClasses } from "../_spacing/globalSpacing";

type IProps = Immutable<{
    value: Value;
}>;

/**
 * Faithful port of the legacy server-rendered
 * ``tsicommon_cms/blocks/accordion-block.html``. The open/close interactivity
 * (toggling ``--is-active`` on the trigger / ``accordion-target--is-active`` on
 * the target, and flipping ``aria-expanded``/``aria-hidden``) is owned by this
 * React component's own state.
 *
 * The ``aria-controls``/``id`` pairing uses ``useId()`` for a stable, SSR-safe,
 * unique value (the id attribute is a11y-only, not pixel-visible).
 */
export const AccordionBlock = (props: IProps): React.JSX.Element => {
    const v = props.value;
    const [isExpanded, setIsExpanded] = React.useState(!!v.is_expanded);
    const targetID = useId();
    const body = v.body ?? [];
    return (
        <div
            className={concatClassNames([
                "accordion",
                isExpanded ? "accordion--is-active" : "",
                getGlobalSpacingClasses(v.spacing),
            ])}
        >
            {/*
             * Raw-inject the header (already a ``.rich-text``-wrapped HTML
             * string) directly into the button to match the legacy markup
             * exactly: legacy placed ``{{ value.header }}`` straight inside the
             * ``<button>``. Using ``<RichTextBlock>`` here would add an extra
             * wrapper div around the ``.rich-text`` element.
             */}
            <button
                className={concatClassNames([
                    "accordion-trigger",
                    isExpanded ? "--is-active" : "",
                ])}
                aria-controls={targetID}
                aria-expanded={isExpanded ? "true" : "false"}
                onClick={(event) => {
                    event.preventDefault();
                    setIsExpanded((value) => !value);
                }}
                dangerouslySetInnerHTML={{ __html: v.header ?? "" }}
            />
            <div
                className={concatClassNames([
                    "accordion-target",
                    isExpanded ? "accordion-target--is-active" : "",
                ])}
                id={targetID}
                aria-hidden={isExpanded ? "false" : "true"}
            >
                {body.map((item) => {
                    if (item.type === "rich_text") {
                        return (
                            <RichTextBlock
                                key={item.id}
                                value={item.value ?? ""}
                            />
                        );
                    }
                    // type === "link": reproduce the shared link-block markup
                    // inline (the shared LinkBlock is not ported yet). Tracking
                    // attrs from the legacy partial are gated on context vars
                    // that are not passed to this block, so they are omitted.
                    return (
                        <a
                            key={item.id}
                            href={
                                item.value.page?.url ??
                                item.value.external_url ??
                                "#"
                            }
                            className={
                                item.value.is_button ? "button" : undefined
                            }
                        >
                            {item.value.display_name}
                        </a>
                    );
                })}
            </div>
        </div>
    );
};
